import { useState, FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Vote, Plus, Trash2, Copy, CheckCircle2, AlertTriangle } from 'lucide-react'
import { rlsvoteAdminApi, ElezioneRlsvoteCreata } from '../../services/api'

function copia(testo: string, label: string) {
  if (!navigator.clipboard?.writeText) {
    toast.error(`Copia non disponibile: seleziona manualmente il campo "${label}"`)
    return
  }
  navigator.clipboard.writeText(testo).then(
    () => toast.success(`${label} copiato negli appunti`),
    () => toast.error(`Impossibile copiare ${label} — selezionalo manualmente`)
  )
}

function RiepilogoElezione({ risultato, onNuova }: { risultato: ElezioneRlsvoteCreata; onNuova: () => void }) {
  const linkVoto = `${window.location.origin}/rlsvote/${risultato.slug_link}`
  const linkAdmin = `${window.location.origin}/rlsvote/admin/${risultato.id}`

  const righe = [
    { label: 'Link pubblico voto', valore: linkVoto },
    { label: 'Link dashboard admin', valore: linkAdmin },
    { label: 'PIN elezione', valore: risultato.pin },
  ]

  return (
    <div className="card p-6 max-w-xl space-y-4">
      <div className="flex items-center gap-2 text-green-700 font-semibold">
        <CheckCircle2 size={18} /> Elezione creata: {risultato.ragione_sociale}
      </div>

      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />
        <span style={{ fontSize: 13, color: '#b91c1c' }}>
          Questi dati non verranno mostrati di nuovo. Copiali e consegnali alla commissione elettorale prima di lasciare questa pagina.
        </span>
      </div>

      <div className="space-y-3">
        {righe.map((r) => (
          <div key={r.label}>
            <label className="label">{r.label}</label>
            <div className="flex gap-2">
              <input className="input flex-1" readOnly value={r.valore} onFocus={(e) => e.target.select()} />
              <button className="btn-secondary" onClick={() => copia(r.valore, r.label)}>
                <Copy size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button className="btn-primary" onClick={onNuova}>Crea un'altra elezione</button>
    </div>
  )
}

export default function CreaElezioneRLS() {
  const [ragioneSociale, setRagioneSociale] = useState('')
  const [dataElezione, setDataElezione] = useState('')
  const [candidati, setCandidati] = useState([''])
  const [pinMaster, setPinMaster] = useState('')
  const [risultato, setRisultato] = useState<ElezioneRlsvoteCreata | null>(null)

  const creaMut = useMutation({
    mutationFn: () =>
      rlsvoteAdminApi.creaElezione(pinMaster, {
        ragione_sociale: ragioneSociale.trim(),
        data_elezione: dataElezione || undefined,
        candidati: candidati.map((c) => c.trim()).filter(Boolean),
      }),
    onSuccess: (res) => {
      setRisultato(res.data)
      setPinMaster('')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const candidatiValidi = candidati.map((c) => c.trim()).filter(Boolean)
  const formValido = ragioneSociale.trim().length > 0 && candidatiValidi.length > 0 && pinMaster.trim().length > 0

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!formValido) return
    creaMut.mutate()
  }

  function resetForm() {
    setRisultato(null)
    setRagioneSociale('')
    setDataElezione('')
    setCandidati([''])
  }

  if (risultato) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1E40AF', marginBottom: 16 }}>RLS Votazione</h1>
        <RiepilogoElezione risultato={risultato} onNuova={resetForm} />
      </div>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1E40AF', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Vote size={22} /> RLS Votazione — Crea elezione
      </h1>
      <p style={{ color: '#64748B', fontSize: 14, marginBottom: 24 }}>
        Crea una nuova elezione per il Rappresentante dei Lavoratori per la Sicurezza. Il modulo di voto è completamente separato da XSophia — richiede il PIN master di setup.
      </p>

      <form onSubmit={handleSubmit} className="card p-6 max-w-xl space-y-4">
        <div>
          <label className="label">Ragione sociale</label>
          <input className="input w-full" value={ragioneSociale} onChange={(e) => setRagioneSociale(e.target.value)} placeholder="Nome Azienda SRL" required />
        </div>

        <div>
          <label className="label">Data elezione (opzionale)</label>
          <input className="input w-full" type="date" value={dataElezione} onChange={(e) => setDataElezione(e.target.value)} />
        </div>

        <div>
          <label className="label">Candidati</label>
          <div className="space-y-2">
            {candidati.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="input flex-1"
                  value={c}
                  placeholder={`Candidato ${i + 1}`}
                  onChange={(e) => setCandidati((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
                />
                {candidati.length > 1 && (
                  <button type="button" className="btn-ghost" onClick={() => setCandidati((prev) => prev.filter((_, idx) => idx !== i))}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary" style={{ marginTop: 8, fontSize: 13 }} onClick={() => setCandidati((prev) => [...prev, ''])}>
            <Plus size={13} /> Aggiungi candidato
          </button>
        </div>

        <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
          <label className="label">PIN master di setup</label>
          <input className="input w-full" type="password" value={pinMaster} onChange={(e) => setPinMaster(e.target.value)} placeholder="PIN di sistema" required />
        </div>

        <button type="submit" className="btn-primary w-full" disabled={!formValido || creaMut.isPending}>
          {creaMut.isPending ? 'Creazione in corso...' : 'Crea elezione'}
        </button>
      </form>
    </div>
  )
}
